// src/pages/SimplePaymentPage.tsx
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { useAuthStore } from '../stores/useAuthStore';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { z } from 'zod';
import { Mail, DollarSign, Smartphone, ChevronLeft, CheckCircle, ShoppingBag, Store, Truck, User, Phone, MapPin } from 'lucide-react';
import { getFullImageUrl } from '../lib/api';

export function SimplePaymentPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { cart, cartOffers, totalPrice, tenantUuid: _tenantUuid, anonymousCartId, config } = location.state || {};

    const { regenerateAnonymousCartId, sessionId, isTakeAway, loginGuest } = useAuthStore();

    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [notes, setNotes] = useState('');
    const [selectedMethod, setSelectedMethod] = useState<string>('');
    const [processing, setProcessing] = useState(false);
    const onlyOneMode = config && (config.accept_delivery !== config.accept_takeaway);
    const [step, setStep] = useState<'mode' | 'info' | 'payment'>(onlyOneMode ? 'info' : 'mode');
    const [orderType, setOrderType] = useState<'pickup' | 'delivery' | null>(
        onlyOneMode ? (config?.accept_takeaway ? 'pickup' : config?.accept_delivery ? 'delivery' : null) : null
    );
    const [pickupName, setPickupName] = useState('');
    const [deliveryData, setDeliveryData] = useState({
        recipient_name: '',
        recipient_phone: '',
        street: '',
        number: '',
        floor: '',
        apartment: '',
        district: '',
        reference: '',
    });

    const currencySymbol = config?.currency_symbol || '$';

    const deliveryFieldsSchema = z.object({
        recipient_name: z.string().min(1, 'El nombre es obligatorio'),
        recipient_phone: z.string().min(7, 'Teléfono inválido (mín. 7 dígitos)'),
        street: z.string().min(1, 'La calle es obligatoria'),
        number: z.string().min(1, 'La altura es obligatoria'),
        district: z.string().min(1, 'El barrio es obligatorio'),
        floor: z.string().optional(),
        apartment: z.string().optional(),
        reference: z.string().optional(),
    });

    // Calcular métodos de pago disponibles según config
    const paymentMethods: { id: string; label: string; icon: React.ReactNode }[] = [];
    if (config?.active_payment_methods?.cash) {
        paymentMethods.push({ id: 'cash', label: 'Efectivo', icon: <DollarSign className="w-5 h-5" /> });
    }
    if (config?.active_payment_methods?.online) {
        paymentMethods.push({ id: 'mercadopago', label: 'Billeteras Virtuales', icon: <Smartphone className="w-5 h-5" /> });
    }
    // Si no hay config o ninguno activo, mostrar ambos como fallback
    if (paymentMethods.length === 0) {
        paymentMethods.push(
            { id: 'cash', label: 'Efectivo', icon: <DollarSign className="w-5 h-5" /> },
            { id: 'mercadopago', label: 'Billeteras Virtuales', icon: <Smartphone className="w-5 h-5" /> },
        );
    }

    // Seleccionar el primero por defecto
    React.useEffect(() => {
        if (paymentMethods.length > 0 && !selectedMethod) {
            setSelectedMethod(paymentMethods[0].id);
        }
    }, []);

    useLayoutEffect(() => {
        if (!config || step !== 'mode') return;

        const onlyDelivery = config.accept_delivery && !config.accept_takeaway;
        const onlyTakeaway = config.accept_takeaway && !config.accept_delivery;

        if (onlyDelivery) {
            setOrderType('delivery');
            setStep('info');
        } else if (onlyTakeaway) {
            setOrderType('pickup');
            setStep('info');
        }
    }, [config, step]);

    const validateEmail = (value: string) => {
        if (!value) return 'El email es requerido';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Ingresá un email válido';
        return '';
    };

    const handleContinue = () => {
        const err = validateEmail(email);
        if (err) {
            setEmailError(err);
            return;
        }
        setEmailError('');
        setStep('payment');
    };

    const handlePay = async () => {
        const hasProducts = cart && cart.length > 0;
        const hasOffers = cartOffers && cartOffers.length > 0;
        if ((!hasProducts && !hasOffers) || !token || !selectedMethod) return;

        if (orderType === 'delivery') {
            const validation = deliveryFieldsSchema.safeParse(deliveryData);
            if (!validation.success) {
                const messages = validation.error?.errors?.map((e: any) => e.message).filter(Boolean) || [];
                alert(messages.length > 0 ? messages.join('\n') : 'Por favor completá todos los campos obligatorios de envío.');
                return;
            }
        }

        setProcessing(true);

        try {
            await loginGuest(email);

            const orderProducts = (cart || []).map((item: any) => ({
                product: item.product.uuid,
                quantity: item.quantity,
                sub_products: item.sub_products?.map((s: any) => s.sub_product_uuid) || [],
            }));

            const orderOffers = (cartOffers || []).map((item: any) => ({
                offer: item.offer.uuid,
                quantity: item.quantity,
                observation: item.observation,
            }));

            const order_data: any = orderType === 'delivery'
                ? {
                    order_type: "delivery",
                    address: `${deliveryData.street} ${deliveryData.number}`,
                    city: deliveryData.district || 'CABA',
                    phone: deliveryData.recipient_phone,
                    recipient_name: deliveryData.recipient_name,
                    recipient_phone: deliveryData.recipient_phone,
                    notes: notes || undefined,
                  }
                : {
                    order_type: "takeaway",
                    pickup_name: pickupName || email.split('@')[0] || "Cliente",
                    notes: notes || undefined,
                  };

            const orderData: any = {
                table: isTakeAway ? null : undefined,
                session: sessionId || null,
                client_identifier: email,
                order_products: orderProducts,
                order_offers: orderOffers,
                observation: notes || undefined,
                order_data,
                metadata: {
                    anonymous_cart_id: anonymousCartId || 'unknown',
                    source: 'simple_ux',
                },
            };

            // Crear la orden
            const order: any = await apiClient.createOrder(orderData);

            if (!order || !order.uuid) {
                throw new Error('No se pudo crear la orden');
            }

            if (selectedMethod === 'mercadopago') {
                // Intentar crear el pago online
                const paymentData: any = {
                    method: 'mercadopago',
                    channel: 'mercadopago',
                    amount: totalPrice?.toString() || '0',
                    table_uuid: null,
                };
                const paymentResp: any = await apiClient.createPayment(paymentData);

                if (paymentResp?.payment_url) {
                    // Guardar el token para volver al menú desde CheckoutSuccess
                    localStorage.setItem('kiero_entry_token', token || '');
                    document.cookie = `kiero_entry_token=${token || ''}; path=/; max-age=3600; SameSite=Lax`;
                    // Redirigir a MercadoPago
                    window.location.href = paymentResp.payment_url;
                    return;
                }
            } else if (selectedMethod === 'cash') {
                // Notificar pago en efectivo al backend
                const paymentData: any = {
                    method: 'cash',
                    channel: 'cash',
                    amount: totalPrice?.toString() || '0',
                    table_uuid: null,
                };
                // Fire and forget o await (el backend crea la intención)
                await apiClient.createPayment(paymentData);
            }

            // Pago en efectivo o flujo finalizado: la orden está creada, setear identifier
            const { setIdentifier } = useAuthStore.getState();
            setIdentifier(email);

            // Regenerar anonymousCartId para el próximo pedido
            regenerateAnonymousCartId();

            navigate('/checkout/success', {
                replace: true,
                state: {
                    uxSimple: true,
                    entryToken: token,
                    orderNumber: order.order_number || order.uuid,
                },
            });
        } catch (err: any) {
            console.error('Error creando orden simple:', err);
            alert('Hubo un error al procesar tu pedido. Por favor intentá de nuevo.');
        } finally {
            setProcessing(false);
        }
    };

    const hasProducts = cart && cart.length > 0;
    const hasOffers = cartOffers && cartOffers.length > 0;
    if (!hasProducts && !hasOffers) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: 'var(--color-bg-page)' }}>
                <div className="text-center">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">No hay productos en tu carrito.</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="text-accent font-bold"
                        style={{ color: 'var(--color-accent-500)' }}
                    >
                        Volver al menú
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-bg-page)' }}>
            {/* Header */}
            <div className="border-b border-gray-100 dark:border-gray-800 px-4 py-4 flex items-center gap-3" style={{ backgroundColor: 'var(--color-bg-card)' }}>
                <button
                    onClick={() => {
                        if (step === 'payment') {
                            setStep('info');
                        } else if (step === 'info') {
                            onlyOneMode ? navigate(-1) : setStep('mode');
                        } else {
                            navigate(-1);
                        }
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                    <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
                <h1 className="text-lg font-black text-gray-900 dark:text-white">
                    {step === 'mode' ? 'Tipo de pedido' : step === 'info' ? 'Tu información' : 'Método de pago'}
                </h1>
                {/* Steps indicator */}
                <div className="ml-auto flex items-center gap-1.5">
                    <div className="w-6 h-1.5 rounded-full" style={step === 'mode' ? { backgroundColor: 'var(--color-accent-500)' } : (step === 'info' || step === 'payment') ? { backgroundColor: '#22c55e' } : { backgroundColor: '#e5e7eb' }} />
                    <div className="w-6 h-1.5 rounded-full" style={step === 'info' ? { backgroundColor: 'var(--color-accent-500)' } : step === 'payment' ? { backgroundColor: '#22c55e' } : { backgroundColor: '#e5e7eb' }} />
                    <div className="w-6 h-1.5 rounded-full" style={step === 'payment' ? { backgroundColor: 'var(--color-accent-500)' } : { backgroundColor: '#e5e7eb' }} />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6 max-w-md mx-auto w-full">
                {/* Resumen del pedido */}
                <div className="rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)' }}>
                    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2">
                        <ShoppingBag className="w-4 h-4 text-accent" style={{ color: 'var(--color-accent-500)' }} />
                        <h2 className="font-bold text-gray-900 dark:text-white text-sm">Tu pedido</h2>
                    </div>
                    <div className="px-5 py-4 space-y-3">
                        {(cart || []).map((item: any) => (
                            <div key={item.product.uuid} className="flex flex-col gap-1">
                                <div className="flex items-center gap-3">
                                    <img
                                        src={getFullImageUrl(item.product.image)}
                                        alt={item.product.name}
                                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.product.name}</p>
                                        <p className="text-xs text-gray-500">x{item.quantity}</p>
                                    </div>
                                    <span className="font-bold text-gray-900 dark:text-white text-sm">
                                        {currencySymbol}{((item.product.price + (item.sub_products?.reduce((acc: number, sub: any) => acc + sub.additional_price, 0) || 0)) * item.quantity).toFixed(2)}
                                    </span>
                                </div>
                                {item.sub_products && item.sub_products.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 ml-[52px] mb-1">
                                        {item.sub_products.map((sub: any) => (
                                            <span key={sub.sub_product_uuid} className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ color: 'var(--color-accent-500)', backgroundColor: 'var(--color-accent-100)' }}>
                                               + {sub.sub_product_name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {(cartOffers || []).map((item: any) => (
                            <div key={item.offer.uuid} className="flex items-center gap-3">
                                <img
                                    src={getFullImageUrl(item.offer.image)}
                                    alt={item.offer.name}
                                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.offer.name}</p>
                                    <p className="text-xs text-gray-500">Oferta x{item.quantity}</p>
                                </div>
                                <span className="font-bold text-gray-900 dark:text-white text-sm">
                                    {currencySymbol}{(item.offer.price * item.quantity).toFixed(2)}
                                </span>
                            </div>
                        ))}
                        <div className="pt-3 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
                            <span className="font-bold text-gray-700 dark:text-gray-300">Total</span>
                            <span className="font-black text-xl text-accent" style={{ color: 'var(--color-accent-500)' }}>{currencySymbol}{totalPrice?.toFixed(2) || '0.00'}</span>
                        </div>
                    </div>
                </div>

                {/* Paso 0: Tipo de pedido */}
                {step === 'mode' && (
                    <div className="rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-5 space-y-4" style={{ backgroundColor: 'var(--color-bg-card)' }}>
                        <h2 className="font-bold text-gray-900 dark:text-white text-center">¿Cómo querés recibir tu pedido?</h2>
                        <div className="grid grid-cols-2 gap-3">
                            {config?.accept_takeaway && (
                                <button
                                    onClick={() => { setOrderType('pickup'); setStep('info'); }}
                                    className="flex flex-col items-center gap-3 p-5 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-accent transition-all text-center"
                                >
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent-100)' }}>
                                        <Store className="w-7 h-7" style={{ color: 'var(--color-accent-500)' }} />
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-900 dark:text-white text-sm">Retiro en local</p>
                                        <p className="text-xs text-gray-500 mt-1">Pasás a buscar tu pedido</p>
                                    </div>
                                </button>
                            )}
                            {config?.accept_delivery && (
                                <button
                                    onClick={() => { setOrderType('delivery'); setStep('info'); }}
                                    className="flex flex-col items-center gap-3 p-5 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-accent transition-all text-center"
                                >
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent-100)' }}>
                                        <Truck className="w-7 h-7" style={{ color: 'var(--color-accent-500)' }} />
                                    </div>
                                    <div>
                                        <p className="font-bold text-gray-900 dark:text-white text-sm">Envío a domicilio</p>
                                        <p className="text-xs text-gray-500 mt-1">Te lo llevamos</p>
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Paso 1: Información */}
                {step === 'info' && (
                    <div className="rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-5 space-y-4" style={{ backgroundColor: 'var(--color-bg-card)' }}>
                        {/* Indicador de modalidad elegida — solo si hay más de una opción */}
                        {config?.accept_delivery && config?.accept_takeaway && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium" style={{ backgroundColor: 'var(--color-accent-100)', color: 'var(--color-accent-600)' }}>
                                {orderType === 'delivery' ? (
                                    <><Truck className="w-4 h-4" /> Envío a domicilio</>
                                ) : (
                                    <><Store className="w-4 h-4" /> Retiro en el local</>
                                )}
                                <button onClick={() => setStep('mode')} className="ml-auto text-xs underline" style={{ color: 'var(--color-accent-600)' }}>
                                    Cambiar
                                </button>
                            </div>
                        )}

                        {/* Campos de delivery */}
                        {orderType === 'delivery' && (
                            <>
                                <h2 className="font-bold text-gray-900 dark:text-white">Datos de envío</h2>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        ¿Quién recibe? <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                        <input
                                            type="text"
                                            value={deliveryData.recipient_name}
                                            onChange={e => setDeliveryData(prev => ({ ...prev, recipient_name: e.target.value }))}
                                            placeholder="Nombre y apellido"
                                            className="w-full pl-10 pr-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all"
                                            style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Teléfono <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                        <input
                                            type="tel"
                                            value={deliveryData.recipient_phone}
                                            onChange={e => setDeliveryData(prev => ({ ...prev, recipient_phone: e.target.value }))}
                                            placeholder="11 1234-5678"
                                            className="w-full pl-10 pr-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all"
                                            style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                            Calle <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                            <input
                                                type="text"
                                                value={deliveryData.street}
                                                onChange={e => setDeliveryData(prev => ({ ...prev, street: e.target.value }))}
                                                placeholder="Av. Corrientes"
                                                className="w-full pl-10 pr-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all"
                                                style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                            Altura <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={deliveryData.number}
                                            onChange={e => setDeliveryData(prev => ({ ...prev, number: e.target.value }))}
                                            placeholder="1234"
                                            className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all"
                                            style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                            Piso <span className="text-gray-400 font-normal">(Opcional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={deliveryData.floor}
                                            onChange={e => setDeliveryData(prev => ({ ...prev, floor: e.target.value }))}
                                            placeholder="3"
                                            className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all"
                                            style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                            Depto <span className="text-gray-400 font-normal">(Opcional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={deliveryData.apartment}
                                            onChange={e => setDeliveryData(prev => ({ ...prev, apartment: e.target.value }))}
                                            placeholder="B"
                                            className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all"
                                            style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Barrio <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={deliveryData.district}
                                        onChange={e => setDeliveryData(prev => ({ ...prev, district: e.target.value }))}
                                        placeholder="Palermo"
                                        className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all"
                                        style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Referencia <span className="text-gray-400 font-normal">(Opcional)</span>
                                    </label>
                                    <textarea
                                        value={deliveryData.reference}
                                        onChange={e => setDeliveryData(prev => ({ ...prev, reference: e.target.value }))}
                                        placeholder="Entre calles, color de puerta..."
                                        rows={2}
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all resize-none"
                                        style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                    />
                                </div>
                            </>
                        )}

                        {/* Campos de pickup */}
                        {orderType === 'pickup' && (
                            <>
                                <h2 className="font-bold text-gray-900 dark:text-white">Datos de compra</h2>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        Nombre completo
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                        <input
                                            type="text"
                                            value={pickupName}
                                            onChange={e => setPickupName(e.target.value)}
                                            placeholder="Tu nombre"
                                            className="w-full pl-10 pr-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all"
                                            style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <h2 className="font-bold text-gray-900 dark:text-white">¿Adónde te enviamos el comprobante?</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Usaremos tu email para identificar tu pedido.</p>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                                    onKeyDown={e => e.key === 'Enter' && handleContinue()}
                                    placeholder="tu@email.com"
                                    className={`w-full pl-10 pr-4 py-3.5 bg-gray-50 dark:bg-gray-800 border ${emailError ? 'border-red-500 focus:ring-red-500/40' : 'border-gray-200 dark:border-gray-700 focus:ring-accent-500/40'} rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all`}
                                    style={emailError ? {} : { '--tw-ring-color': 'var(--color-accent-500)' } as any}
                                />
                            </div>
                            {emailError && <p className="text-red-500 text-xs mt-1.5 font-medium">{emailError}</p>}
                        </div>
                        <div className="pt-2">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                Notas adicionales <span className="text-gray-400 font-normal">(Opcional)</span>
                            </label>
                            <textarea
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Ej: Alguna indicación especial para tu pedido..."
                                rows={2}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-accent-500/40 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 outline-none transition-all resize-none"
                                style={{ '--tw-ring-color': 'var(--color-accent-500)' } as any}
                            />
                        </div>
                    </div>
                )}

                {/* Paso 2: Método de pago */}
                {step === 'payment' && (
                    <div className="rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-5 space-y-4" style={{ backgroundColor: 'var(--color-bg-card)' }}>
                        <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                Pedido para: <span className="font-semibold text-gray-900 dark:text-white">{email}</span>
                            </span>
                        </div>
                        <h2 className="font-bold text-gray-900 dark:text-white">¿Cómo vas a pagar?</h2>
                        <div className="space-y-2">
                            {paymentMethods.map(method => (
                                <label
                                    key={method.id}
                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedMethod === method.id
                                        ? 'border-accent bg-accent-50'
                                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 hover:border-gray-300 dark:hover:border-gray-600'
                                        }`}
                                    style={selectedMethod === method.id ? { borderColor: 'var(--color-accent-500)', backgroundColor: 'var(--color-accent-100)' } : {}}
                                >
                                    <input
                                        type="radio"
                                        name="method"
                                        value={method.id}
                                        checked={selectedMethod === method.id}
                                        onChange={() => setSelectedMethod(method.id)}
                                        className="sr-only"
                                    />
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center`} style={selectedMethod === method.id ? { borderColor: 'var(--color-accent-500)', backgroundColor: 'var(--color-accent-500)' } : { borderColor: '#d1d5db' }}>
                                        {selectedMethod === method.id && <div className="w-2 h-2 bg-white rounded-full" />}
                                    </div>
                                    <div className={`transition-colors`} style={selectedMethod === method.id ? { color: 'var(--color-accent-500)' } : { color: '#9ca3af' }}>
                                        {method.icon}
                                    </div>
                                    <span className="font-semibold text-gray-900 dark:text-gray-200" style={selectedMethod === method.id ? { color: 'var(--color-accent-600)' } : {}}>
                                        {method.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer CTA */}
            {step !== 'mode' && (
            <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-5 max-w-md mx-auto w-full" style={{ backgroundColor: 'var(--color-bg-card)' }}>
                {step === 'info' ? (
                    <button
                        onClick={handleContinue}
                        className="w-full btn-accent py-4 rounded-2xl font-bold text-base shadow-lg transition-all"
                        style={{ boxShadow: '0 10px 15px -3px var(--color-accent-500)' }}
                    >
                        Continuar →
                    </button>
                ) : (
                    <button
                        onClick={handlePay}
                        disabled={processing || !selectedMethod}
                        className="w-full btn-accent disabled:bg-gray-300 text-white py-4 rounded-2xl font-bold text-base shadow-lg transition-all flex items-center justify-center gap-2"
                        style={processing || !selectedMethod ? {} : { backgroundColor: 'var(--color-accent-500)', boxShadow: '0 10px 15px -3px var(--color-accent-500)' }}
                    >
                        {processing ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Procesando...
                            </>
                        ) : (
                            `Pagar ${currencySymbol}${totalPrice?.toFixed(2) || '0.00'}`
                        )}
                    </button>
                )}
            </div>
            )}

            {processing && (
                <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center">
                    <LoadingSpinner message="Procesando tu pedido..." />
                </div>
            )}
        </div>
    );
}
